import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle } from 'lucide-react'
import { useMemo, useState } from 'react'

import { fetchDelegations, fetchStakingParams } from '@/api'
import CopyWithTooltip from '@/common/CopyWithTooltip'
import GradientWrapper from '@/common/GradientWrapper'
import ResponsiveModalDrawer from '@/common/ResponsiveModalDrawer'
import ValidatorSelect from '@/common/ValidatorSelect'
import { bridgelessChainInfo } from '@/config'
import { useKeplrWallet } from '@/contexts/KeplrWalletProvider'
import {
  emitter,
  ErrorHandler,
  formatAddress,
  formatBridgeAtomic,
  formatDurationString,
  parseSharesAmount,
} from '@/helpers'
import {
  buildNativeUndelegateMsg,
  NativeUndelegateError,
} from '@/libs/keplr/messages'
import { queryKeys } from '@/queries/keys'
import UiRing from '@/ui/loaders/UiRing'
import { UiButton } from '@/ui/UiButton'

interface NativeUndelegateModalProps {
  open: boolean
  onClose: () => void
}

export default function NativeUndelegateModal({
  open,
  onClose,
}: NativeUndelegateModalProps) {
  const { address, signAndBroadcast } = useKeplrWallet()
  const queryClient = useQueryClient()

  // Fetch delegation
  const { data: delegationData, isLoading } = useQuery({
    queryKey: queryKeys.staking.delegations(address ?? ''),
    enabled: Boolean(open && address),
    queryFn: async () => fetchDelegations(address!),
    staleTime: 30_000,
    refetchInterval: 30_000,
  })

  // Fetch staking params
  const { data: stakingParams } = useQuery({
    queryKey: queryKeys.staking.params(),
    enabled: open,
    queryFn: fetchStakingParams,
    staleTime: 300_000,
  })

  const unbondingDuration = useMemo(() => {
    return formatDurationString(stakingParams?.unbonding_time)
  }, [stakingParams])

  // Build list of all delegations with their amounts
  const delegations = useMemo(() => {
    return (delegationData?.delegation_responses ?? [])
      .map(delegationResponse => {
        const amountAtomic = parseSharesAmount(
          delegationResponse?.delegation?.shares,
        )
        const validator =
          delegationResponse?.delegation?.validator_address ?? ''

        if (!amountAtomic || !validator) return null

        return {
          validator,
          amountAtomic,
        }
      })
      .filter(
        (d): d is { validator: string; amountAtomic: string } => d !== null,
      )
  }, [delegationData])

  // Selected validator for undelegation
  const [selectedValidator, setSelectedValidator] = useState('')

  // Find the delegation for selected validator
  const selectedDelegation = useMemo(() => {
    return delegations.find(d => d.validator === selectedValidator)
  }, [delegations, selectedValidator])

  const delegatedAmountAtomic = useMemo(() => {
    try {
      return BigInt(selectedDelegation?.amountAtomic ?? '0')
    } catch {
      return 0n
    }
  }, [selectedDelegation])

  const hasDelegation = delegations.length > 0 && delegatedAmountAtomic > 0n

  // Auto-select first validator when modal opens
  useMemo(() => {
    if (open && delegations.length > 0 && !selectedValidator) {
      setSelectedValidator(delegations[0].validator)
    }
  }, [open, delegations, selectedValidator])

  const undelegateTx = useMutation({
    mutationKey: queryKeys.tx.nativeUndelegate(address ?? ''),
    mutationFn: async () => {
      if (!address) {
        throw new Error('Wallet not connected')
      }

      if (!selectedDelegation || !selectedValidator) {
        throw new Error('No delegation selected')
      }

      const msg = buildNativeUndelegateMsg({
        delegatorAddress: address,
        validatorAddress: selectedValidator,
        amount: {
          denom: bridgelessChainInfo.stakeCurrency.coinMinimalDenom,
          amount: selectedDelegation.amountAtomic,
        },
      })

      return signAndBroadcast([msg])
    },
    onSuccess: txHash => {
      emitter.emit('success', {
        title: 'Undelegation sent',
        message: `${formatAddress(txHash)}`,
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.staking.delegations(address ?? ''),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.bank.balances(address ?? ''),
      })
      onClose()
    },
    onError: error => {
      ErrorHandler.process(NativeUndelegateError.fromUnknown(error))
    },
  })

  const isSubmitting = undelegateTx.isPending

  return (
    <ResponsiveModalDrawer
      open={open}
      onOpenChange={v => !v && !isSubmitting && onClose()}
      title={<span className='text-foreground'>Undelegate Native Token</span>}
      content={
        <form
          onSubmit={event => {
            event.preventDefault()
            undelegateTx.mutate()
          }}
          className='text-foreground mt-4 flex flex-col gap-4 p-1'
        >
          <div className='flex flex-col gap-1'>
            <span className='text-muted-foreground text-xs'>Your Address</span>
            <div className='bg-input flex h-12 items-center gap-2 rounded-lg px-3 py-2'>
              <span className='muted-foreground text-sm'>
                {formatAddress(address ?? '', 20, 15)}
              </span>
              <div className='ml-auto'>
                <CopyWithTooltip value={address ?? ''} />
              </div>
            </div>
          </div>

          <div className='flex flex-col gap-1'>
            <span className='text-muted-foreground text-xs'>Validator</span>
            <ValidatorSelect
              value={selectedValidator}
              disabled={isLoading || delegations.length === 0}
              triggerClassName='w-full'
              contentClassName='w-full'
              onChange={v => setSelectedValidator(v)}
            />
            {delegations.length > 1 && (
              <span className='text-muted-foreground text-xs'>
                You have {delegations.length} delegations. Select validator to
                undelegate.
              </span>
            )}
          </div>

          {selectedDelegation && delegatedAmountAtomic > 0n ? (
            <div className='flex flex-col gap-1'>
              <span className='text-muted-foreground text-xs'>Amount</span>
              <GradientWrapper>
                <div className='bg-input text-foreground flex h-12 w-full items-center rounded-lg px-3 py-2 text-sm font-medium'>
                  {isLoading
                    ? '…'
                    : `${formatBridgeAtomic(selectedDelegation.amountAtomic)} BRIDGE`}
                </div>
              </GradientWrapper>
              <span className='text-muted-foreground text-xs'>
                (Full delegation will be undelegated)
              </span>
            </div>
          ) : (
            <div className='flex flex-col gap-2 rounded-lg border border-yellow-500/30 bg-yellow-500/10 p-3'>
              <div className='flex items-center gap-2'>
                <AlertTriangle className='size-4 text-yellow-500' />
                <span className='text-sm font-semibold text-yellow-500'>
                  No Active Delegation
                </span>
              </div>
              <p className='text-muted-foreground text-xs'>
                {selectedValidator
                  ? 'You have no delegation to this validator.'
                  : 'Please select a validator to view delegation.'}
              </p>
            </div>
          )}

          {unbondingDuration && hasDelegation && (
            <div className='flex flex-col gap-2 rounded-lg border border-yellow-500/30 bg-yellow-500/10 p-3'>
              <div className='flex items-center gap-2'>
                <AlertTriangle className='size-4 text-yellow-500' />
                <span className='text-sm font-semibold text-yellow-500'>
                  Unbonding Period
                </span>
              </div>
              <p className='text-muted-foreground text-xs'>
                If you undelegate, your funds will be locked for
                <span className='text-foreground px-1 font-semibold'>
                  {unbondingDuration}
                </span>
                before they become available for withdrawal.
              </p>
            </div>
          )}
        </form>
      }
      footer={
        <div className='flex w-full gap-2'>
          <UiButton
            variant='outline2'
            className='h-12 flex-1'
            onClick={onClose}
            disabled={isSubmitting}
          >
            Close
          </UiButton>

          <UiButton
            variant='outline'
            className='h-12 flex-1'
            disabled={isSubmitting || !hasDelegation || isLoading}
            onClick={() => undelegateTx.mutate()}
          >
            {isSubmitting ? (
              <span className='flex items-center gap-2'>
                <UiRing size='15' stroke='2' speed='1.65' color='white' />
                Undelegating…
              </span>
            ) : (
              'Undelegate'
            )}
          </UiButton>
        </div>
      }
      modalProps={{ className: 'bg-card text-foreground rounded-lg max-w-md' }}
      drawerProps={{ className: 'bg-card text-foreground rounded-lg' }}
    />
  )
}
